/**
 * InputHandler System
 * 
 * Handles keyboard and mouse input for the game scene.
 */

import Phaser from 'phaser';
import { ShipSprite } from './ShipRenderer';

// ============ INTERFACES ============

export interface InputCallbacks {
  onEscape: () => void;
  onSpace: () => void;
  onFocusToggle?: () => void;  // SPACE to toggle cannon focus minigame (when in combat)
  onFishingToggle?: () => void;  // SPACE while fishing to toggle fishing minigame
  isFishingMinigameActive?: () => boolean;  // Whether the fishing minigame UI is currently active
  isCannonFocusAvailable?: () => boolean;  // Whether cannon focus can be activated (in combat, in range)
  isCannonFocusActive?: () => boolean;  // Whether cannon focus minigame is currently active
  onSprintStart?: () => void;  // Shift key down - start sprinting
  onSprintEnd?: () => void;    // Shift key up - stop sprinting
  onWildcardPower?: () => void;  // Q key - activate signature move
  onWASDMove?: (direction: 'up' | 'down' | 'left' | 'right', isPressed: boolean) => void;  // WASD movement
  onClickShip: (shipId: string) => void;
  onClickEmpty: (worldX: number, worldY: number) => void;
  onRightClickShip?: (shipId: string, screenX: number, screenY: number, shipData: ShipSprite['shipData']) => void;  // Right-click on ship for context menu
  onRightClickEmpty?: (worldX: number, worldY: number, screenX: number, screenY: number) => void;  // Right-click on empty space for "Travel Here"
  onRightClickIsland?: (worldX: number, worldY: number, screenX: number, screenY: number) => void;  // Right-click on island for context menu
  onRightClickRiftScar?: (worldX: number, worldY: number, screenX: number, screenY: number) => void;  // Right-click on rift scar for context menu
  getShipAtPosition: (x: number, y: number) => ShipSprite | null;
  getLocalPlayerId: () => string;
  isInputBlocked?: () => boolean;  // Check if input should be blocked (e.g., player dead)
  isFishing?: () => boolean;  // Check if player is currently fishing
  hasIslandAt?: (worldX: number, worldY: number) => boolean;  // Check if an island is under the pointer (for context menus)
  hasRiftScarAt?: (worldX: number, worldY: number) => boolean;  // Check if a rift scar is under the pointer
  isConstructionMode?: () => boolean;  // Check if construction mode is active
  onConstructionClick?: (worldX: number, worldY: number) => void;  // Left-click to place construction
  onConstructionMove?: (worldX: number, worldY: number) => void;  // Mouse move to update preview
  onConstructionExit?: () => void;  // ESC to exit construction mode
  isSignatureCastMode?: () => boolean;  // Check if signature cast mode is active
  onSignatureCastClick?: (worldX: number, worldY: number) => void;  // Left-click to cast signature move
  onSignatureCastMove?: (worldX: number, worldY: number) => void;  // Mouse move to update cast preview
  onSignatureCastExit?: () => void;  // ESC to exit signature cast mode
  showNothingInterestingHappens?: () => void;  // Show "Nothing interesting happens" toast
}

// ============ INPUT HANDLER SYSTEM ============

export class InputHandler {
  private scene: Phaser.Scene;
  private callbacks: InputCallbacks;
  private keys: { [key: string]: Phaser.Input.Keyboard.Key } = {};

  constructor(scene: Phaser.Scene, callbacks: InputCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  // ============ PUBLIC API ============

  /**
   * Set up all input handlers
   */
  public setup(): void {
    this.setupKeyboard();
    this.setupMouse();
    console.log(`🎮 Input setup complete`);
  }

  /**
   * Check if a key is currently pressed
   */
  public isKeyDown(key: string): boolean {
    return this.keys[key]?.isDown ?? false;
  }

  /**
   * Check if a text input or textarea is currently focused
   * This prevents game keybinds from firing while typing
   */
  private isTextInputFocused(): boolean {
    const activeElement = document.activeElement;
    return (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement
    );
  }

  // ============ PRIVATE METHODS ============

  private setupKeyboard(): void {
    if (this.scene.input.keyboard) {
      this.keys = {
        SPACE: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
        ESC: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC),
        F: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F),
        E: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E),
        SHIFT: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT),
        W: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
        A: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
        S: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
        D: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
        UP: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
        DOWN: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN),
        LEFT: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
        RIGHT: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
      };
      
      // Also listen to native keyboard events for shift (works for both left and right)
      // This is a fallback in case Phaser's SHIFT key code doesn't work
      if (this.scene.input.keyboard) {
        this.scene.input.keyboard.on('keydown-SHIFT', () => {
          console.log('🏃 [SPRINT] Native SHIFT keydown event');
          if (this.callbacks.onSprintStart) {
            this.callbacks.onSprintStart();
          }
        });
        
        this.scene.input.keyboard.on('keyup-SHIFT', () => {
          console.log('🏃 [SPRINT] Native SHIFT keyup event');
          if (this.callbacks.onSprintEnd) {
            this.callbacks.onSprintEnd();
          }
        });
      }
      
      // Stop movement / disengage / exit construction mode / exit signature cast mode
      this.keys.ESC.on('down', () => {
        // Skip if text input is focused (allow ESC to close input naturally)
        if (this.isTextInputFocused()) {
          return;
        }
        // If in signature cast mode, exit it first
        if (this.callbacks.isSignatureCastMode?.() && this.callbacks.onSignatureCastExit) {
          this.callbacks.onSignatureCastExit();
          return;
        }
        // If in construction mode, exit it first
        if (this.callbacks.isConstructionMode?.() && this.callbacks.onConstructionExit) {
          this.callbacks.onConstructionExit();
          return;
        }
        this.callbacks.onEscape();
      });
      
      // SPACE: Priority order:
      // 1. If text input focused -> let input handle it (don't intercept)
      // 2. If fishing minigame active -> let minigame handle it (don't intercept)
      // 3. If cannon focus minigame active -> let minigame handle it (don't intercept)
      // 4. If fishing (but minigame not active) -> toggle fishing minigame
      // 5. If cannon focus available (in combat, in range) -> toggle cannon focus
      // 6. Otherwise -> stop movement
      this.keys.SPACE.on('down', () => {
        // Skip if text input is focused (allow SPACE to insert space in input)
        if (this.isTextInputFocused()) {
          return;
        }

        const isFishingMinigameActive = this.callbacks.isFishingMinigameActive?.() ?? false;
        const isCannonFocusActive = this.callbacks.isCannonFocusActive?.() ?? false;
        const isFishing = this.callbacks.isFishing?.() ?? false;
        
        console.log(`🎣 SPACE pressed - isFishing: ${isFishing}, isFishingMinigameActive: ${isFishingMinigameActive}, isCannonFocusActive: ${isCannonFocusActive}`);
        
        // If either minigame is active, don't intercept - let the minigame handle SPACE
        if (isFishingMinigameActive || isCannonFocusActive) {
          console.log(`🎣 SPACE blocked - minigame active`);
          return;
        }
        
        // If fishing, toggle fishing minigame
        if (isFishing && this.callbacks.onFishingToggle) {
          console.log(`🎣 SPACE - toggling fishing minigame`);
          this.callbacks.onFishingToggle();
          return;
        }
        
        // If cannon focus is available (in combat, in range), toggle it
        if (this.callbacks.isCannonFocusAvailable?.() && this.callbacks.onFocusToggle) {
          console.log(`🎣 SPACE - toggling cannon focus`);
          this.callbacks.onFocusToggle();
          return;
        }
        
        // Default: stop movement
        console.log(`🎣 SPACE - stopping movement`);
        this.callbacks.onSpace();
      });
      
      // Sprint with Shift key
      const handleSprintStart = () => {
        console.log('🏃 [SPRINT] Shift key pressed (Phaser key event)');
        if (this.callbacks.onSprintStart) {
          this.callbacks.onSprintStart();
        }
      };
      
      const handleSprintEnd = () => {
        console.log('🏃 [SPRINT] Shift key released (Phaser key event)');
        if (this.callbacks.onSprintEnd) {
          this.callbacks.onSprintEnd();
        }
      };
      
      // Listen to Phaser's SHIFT key events
      this.keys.SHIFT.on('down', handleSprintStart);
      this.keys.SHIFT.on('up', handleSprintEnd);
      
      // E key - activate signature move
      this.keys.E.on('down', () => {
        // Skip if text input is focused
        if (this.isTextInputFocused()) {
          return;
        }
        if (this.callbacks.onWildcardPower) {
          this.callbacks.onWildcardPower();
        }
      });
      
      // WASD movement - only when not in cannon focus or signature cast mode
      const handleWASDDown = (key: string, direction: 'up' | 'down' | 'left' | 'right') => {
        // Skip if text input is focused (allow WASD in chat)
        if (this.isTextInputFocused()) {
          return;
        }
        const isCannonFocusActive = this.callbacks.isCannonFocusActive?.() ?? false;
        const isSignatureCastActive = this.callbacks.isSignatureCastMode?.() ?? false;
        if (isCannonFocusActive || isSignatureCastActive) {
          return; // Block WASD in minigames/special modes
        }
        if (this.callbacks.onWASDMove) {
          this.callbacks.onWASDMove(direction, true);
        }
      };
      
      const handleWASDUp = (key: string, direction: 'up' | 'down' | 'left' | 'right') => {
        // Skip if text input is focused (allow WASD in chat)
        if (this.isTextInputFocused()) {
          return;
        }
        const isCannonFocusActive = this.callbacks.isCannonFocusActive?.() ?? false;
        const isSignatureCastActive = this.callbacks.isSignatureCastMode?.() ?? false;
        if (isCannonFocusActive || isSignatureCastActive) {
          return; // Block WASD in minigames/special modes
        }
        if (this.callbacks.onWASDMove) {
          this.callbacks.onWASDMove(direction, false);
        }
      };
      
      this.keys.W.on('down', () => handleWASDDown('W', 'up'));
      this.keys.W.on('up', () => handleWASDUp('W', 'up'));
      this.keys.A.on('down', () => handleWASDDown('A', 'left'));
      this.keys.A.on('up', () => handleWASDUp('A', 'left'));
      this.keys.S.on('down', () => handleWASDDown('S', 'down'));
      this.keys.S.on('up', () => handleWASDUp('S', 'down'));
      this.keys.D.on('down', () => handleWASDDown('D', 'right'));
      this.keys.D.on('up', () => handleWASDUp('D', 'right'));
      
      // Arrow keys do the same as WASD
      this.keys.UP.on('down', () => handleWASDDown('UP', 'up'));
      this.keys.UP.on('up', () => handleWASDUp('UP', 'up'));
      this.keys.LEFT.on('down', () => handleWASDDown('LEFT', 'left'));
      this.keys.LEFT.on('up', () => handleWASDUp('LEFT', 'left'));
      this.keys.DOWN.on('down', () => handleWASDDown('DOWN', 'down'));
      this.keys.DOWN.on('up', () => handleWASDUp('DOWN', 'down'));
      this.keys.RIGHT.on('down', () => handleWASDDown('RIGHT', 'right'));
      this.keys.RIGHT.on('up', () => handleWASDUp('RIGHT', 'right'));
    }
  }

  private setupMouse(): void {
    // Mouse move for construction preview and signature cast preview
    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Update construction preview if in construction mode
      if (this.callbacks.isConstructionMode?.() && this.callbacks.onConstructionMove) {
        this.callbacks.onConstructionMove(pointer.worldX, pointer.worldY);
      }
      
      // Update signature cast preview if in cast mode
      if (this.callbacks.isSignatureCastMode?.() && this.callbacks.onSignatureCastMove) {
        this.callbacks.onSignatureCastMove(pointer.worldX, pointer.worldY);
      }
    });

    // Click-based controls - LEFT CLICK ONLY for movement/combat
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only process left-click for movement/combat
      // Right-click is reserved for context menus (island research, fishing spot research, etc.)
      if (!pointer.leftButtonDown()) {
        return;
      }
      
      // Block input if player is dead or otherwise blocked
      if (this.callbacks.isInputBlocked?.()) {
        console.log(`🖱️ Click blocked - input is disabled`);
        this.callbacks.showNothingInterestingHappens?.();
        return;
      }

      // If in signature cast mode, handle cast placement
      if (this.callbacks.isSignatureCastMode?.()) {
        console.log(`⚡ Signature cast click at world (${pointer.worldX}, ${pointer.worldY})`);
        if (this.callbacks.onSignatureCastClick) {
          this.callbacks.onSignatureCastClick(pointer.worldX, pointer.worldY);
        }
        return;
      }
      
      // If in construction mode, handle construction placement
      if (this.callbacks.isConstructionMode?.()) {
        console.log(`🔨 Construction click at world (${pointer.worldX}, ${pointer.worldY})`);
        if (this.callbacks.onConstructionClick) {
          this.callbacks.onConstructionClick(pointer.worldX, pointer.worldY);
        }
        return;
      }
      
      // If fishing, auto-disengage and move to clicked tile
      if (this.callbacks.isFishing?.()) {
        console.log(`🎣 Fishing disengaged - moving to clicked tile`);
        // Stop fishing (this happens when movement starts)
        this.callbacks.onClickEmpty(pointer.worldX, pointer.worldY);
        return;
      }
      
      console.log(`🖱️ Left-click detected at world (${pointer.worldX}, ${pointer.worldY})`);
      
      const clickedShip = this.callbacks.getShipAtPosition(pointer.worldX, pointer.worldY);
      const localPlayerId = this.callbacks.getLocalPlayerId();
      
      if (clickedShip && clickedShip.shipData.ownerId !== localPlayerId) {
        // Click on enemy = engage target (move toward + auto-fire when in range)
        if (clickedShip.shipId) {
          console.log(`⚔️ Clicked on enemy ship: ${clickedShip.shipId}`);
          this.callbacks.onClickShip(clickedShip.shipId);
        }
      } else {
        // Click on empty space = move there (and disengage current target)
        console.log(`🚶 Moving to empty space`);
        this.callbacks.onClickEmpty(pointer.worldX, pointer.worldY);
      }
    });
    
    // Handle right-click for context menus
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only process right-click for context menus
      if (!pointer.rightButtonDown()) {
        return;
      }
      
      // Block right-click in signature cast mode
      if (this.callbacks.isSignatureCastMode?.()) {
        console.log(`⚡ Right-click blocked - signature cast mode active`);
        return;
      }
      
      console.log(`🖱️ InputHandler: Right-click detected at (${pointer.worldX}, ${pointer.worldY})`);
      
      // Block input if player is dead or otherwise blocked
      if (this.callbacks.isInputBlocked?.()) {
        console.log(`🖱️ InputHandler: Input blocked`);
        return;
      }
      
      const clickedShip = this.callbacks.getShipAtPosition(pointer.worldX, pointer.worldY);
      const localPlayerId = this.callbacks.getLocalPlayerId();
      
      // Calculate screen position for context menu
      const camera = this.scene.cameras.main;
      const screenX = pointer.worldX - camera.scrollX;
      const screenY = pointer.worldY - camera.scrollY;
      
      if (clickedShip && clickedShip.shipData.ownerId !== localPlayerId) {
        // Right-click on enemy ship = show context menu with Investigate/Engage
        if (clickedShip.shipId) {
          console.log(`🖱️ Right-click on enemy ship: ${clickedShip.shipId}`);
          if (this.callbacks.onRightClickShip) {
            this.callbacks.onRightClickShip(clickedShip.shipId, screenX, screenY, clickedShip.shipData);
          }
        }
      } else if (!clickedShip) {
        // Check if there's a rift scar under the pointer
        if (this.callbacks.hasRiftScarAt && this.callbacks.hasRiftScarAt(pointer.worldX, pointer.worldY)) {
          // Right-click on rift scar = show rift scar context menu
          console.log(`🖱️ Right-click on rift scar at (${pointer.worldX}, ${pointer.worldY})`);
          if (this.callbacks.onRightClickRiftScar) {
            this.callbacks.onRightClickRiftScar(pointer.worldX, pointer.worldY, screenX, screenY);
          }
          return;
        }
        
        // Check if there's an island under the pointer
        if (this.callbacks.hasIslandAt && this.callbacks.hasIslandAt(pointer.worldX, pointer.worldY)) {
          // Right-click on island = show island context menu
          console.log(`🖱️ Right-click on island at (${pointer.worldX}, ${pointer.worldY})`);
          if (this.callbacks.onRightClickIsland) {
            this.callbacks.onRightClickIsland(pointer.worldX, pointer.worldY, screenX, screenY);
          }
          return;
        }

        // Right-click on empty space = show "Travel Here" context menu
        console.log(`🖱️ Right-click on empty space`);
        if (this.callbacks.onRightClickEmpty) {
          this.callbacks.onRightClickEmpty(pointer.worldX, pointer.worldY, screenX, screenY);
        }
      }
    });
    
    // Disable browser context menu for right-click (we handle it ourselves)
    this.scene.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }
}
